import { formatCompact, formatDecimal, formatInt, formatMs, formatPct } from '../../lib/format.ts'
import type { MetricsPayload } from '../../lib/sse/types.ts'
import { Metric } from '../primitives/Metric.tsx'
import { Meter, utilisationTone } from '../primitives/Meter.tsx'
import type { Threshold } from '../primitives/Meter.tsx'
import { Panel } from '../primitives/Panel.tsx'

const THRESHOLDS: readonly Threshold[] = [
  { at: 60, label: '60% — pressão' },
  { at: 85, label: '85% — saturação' },
]

/**
 * Ingestion pipeline.
 *
 * Queue depth leads because it is the earliest signal in the system: by the time
 * latency or rejections move, the backlog has already been growing for minutes.
 * It is rendered against its ceiling rather than as a bare number, since 12% and
 * 12% of what are different facts, and the 60/85 marks are where an operator
 * actually changes behaviour.
 */
export function IngestPanel({
  metrics,
  className,
}: {
  metrics: MetricsPayload | null
  className?: string
}) {
  const ingest = metrics?.ingest ?? null
  const depthPct = ingest?.channel_depth_pct ?? 0
  const tone = ingest === null ? 'neutral' : utilisationTone(depthPct)

  const valueText =
    ingest === null
      ? 'sem dados'
      : `${formatInt(ingest.channel_depth)} de ${formatInt(ingest.channel_capacity)} eventos (${formatPct(depthPct)})`

  return (
    <Panel title="Pipeline de ingestão" className={className}>
      <div className="flex h-full flex-col gap-3">
        <div>
          <div className="mb-1 flex items-end justify-between gap-2">
            <Metric
              label="profundidade da fila"
              value={formatPct(depthPct)}
              size="lg"
              tone={tone}
              note={
                ingest === null ? undefined : (
                  <>
                    <span className="tnum">{formatInt(ingest.channel_depth)}</span> de{' '}
                    <span className="tnum">{formatCompact(ingest.channel_capacity)}</span> em buffer
                  </>
                )
              }
            />
          </div>
          <Meter
            pct={depthPct}
            tone={tone}
            label="Profundidade da fila de ingestão"
            valueText={valueText}
            thresholds={THRESHOLDS}
          />
        </div>

        <div className="grid grid-cols-3 gap-x-3 gap-y-2 border-t border-line-subtle pt-3">
          <Metric
            label="aceitos"
            value={formatInt(ingest?.events_per_sec_accepted)}
            unit="ev/s"
            tone="accent"
          />
          <Metric label="recebidos" value={formatInt(ingest?.events_per_sec_received)} unit="ev/s" />
          <Metric
            label="rejeitados"
            value={formatInt(ingest?.events_per_sec_rejected)}
            unit="ev/s"
            tone={(ingest?.events_per_sec_rejected ?? 0) > 0 ? 'warn' : 'neutral'}
          />
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-line-subtle pt-3">
          <Metric
            label="lote médio"
            value={formatInt(metrics?.batching.avg_batch_size)}
            unit="eventos"
          />
          <Metric label="flush médio" value={formatMs(metrics?.batching.avg_flush_ms)} />
          <Metric
            label="taxa de aceite"
            value={
              ingest === null || ingest.events_per_sec_received === 0
                ? '—'
                : formatDecimal(
                    (ingest.events_per_sec_accepted / ingest.events_per_sec_received) * 100,
                    2,
                  ) + '%'
            }
          />
          <Metric label="conexões ativas" value={formatInt(ingest?.active_connections)} />
        </div>
      </div>
    </Panel>
  )
}
